import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DropdownTextField from "../form-components/DropdownTextField";
import MapSpotSelector from "../common/MapSpotSelector";
import IconPickerModal from "./IconPickerModal";
import { v4 as uuid } from 'uuid';
import LocalText from "../common/LocalText";
import { hasRequiredName } from '../../utils/validators';
import { IconMap } from '../../utils/IconMap';

const CreateBoothModal = ({ open, onClose, onSave, usedMapSpotIds = new Set() }) => {
  const [localizedPairs, setLocalizedPairs] = useState({ key: uuid(), values: [] });
  const [mapSpotId, setMapSpotId] = useState('');
  const [iconKey, setIconKey] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [iconError, setIconError] = useState(false);

  // Modal stays mounted with `open` toggled by the parent — reset on every fresh open so
  // values from a previous Cancel/close don't linger.
  useEffect(() => {
    if (!open) return;
    setLocalizedPairs({ key: uuid(), values: [] });
    setMapSpotId('');
    setIconKey('');
    setIconError(false);
  }, [open]);

  const handleSave = async () => {
    if (!iconKey) { setIconError(true); return; }
    try {
      await onSave({ localizedPairs, mapSpotId, iconKey: iconKey || null, modifiedBy: 'system' });
      onClose();
    } catch {
      // error shown globally via axios interceptor → toast
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
    >
      <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', pr: 6, py: 2.5 }}>
        <Typography variant="h6" fontWeight={700} component="span">
          <LocalText>Create Booth</LocalText>
        </Typography>
        <IconButton
          onClick={(e) => { e.currentTarget.blur(); onClose(); }}
          size="small"
          sx={{ position: 'absolute', right: 10, top: 12, color: 'white' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Box display="flex" flexDirection="column" gap={2}>
          <MapSpotSelector
            value={mapSpotId}
            onChange={(id) => setMapSpotId(id)}
            required
            disabledIds={usedMapSpotIds}
          />
          <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: 'text.secondary', mb: -1 }}>
            <LocalText>Booth Name</LocalText>
            <span style={{ color: '#d32f2f', marginLeft: 3 }}>*</span>
          </Typography>
          <DropdownTextField
            label="Booth Name"
            values={localizedPairs?.values}
            onChange={(newValues) => setLocalizedPairs((prev) => ({ ...prev, values: newValues }))}
            attribute="value"
            required
          />

          <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: 'text.secondary', mb: -1 }}>
            <LocalText>Icon</LocalText>
            <span style={{ color: '#d32f2f', marginLeft: 3 }}>*</span>
          </Typography>
          <Box
            onClick={() => setPickerOpen(true)}
            sx={{
              display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer',
              border: '1px solid', borderColor: iconError ? '#d32f2f' : 'grey.300', borderRadius: 2, p: 1.25,
              '&:hover': { borderColor: 'primary.main', bgcolor: 'rgba(10,50,109,0.03)' },
            }}
          >
            <Box sx={{
              bgcolor: '#0a326d', borderRadius: '10px', width: 40, height: 40,
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              {iconKey && (
                <Box component="img" src={IconMap[`bth_${iconKey}`]} alt="" sx={{ width: 24, height: 24 }} />
              )}
            </Box>
            <Typography variant="body2" sx={{ color: iconKey ? 'text.primary' : 'text.secondary' }}>
              <LocalText>{iconKey ? 'Change icon' : 'Choose an icon'}</LocalText>
            </Typography>
          </Box>
          {iconError && (
            <Typography variant="caption" sx={{ color: '#d32f2f', mt: -1.5 }}>
              <LocalText>Required</LocalText>
            </Typography>
          )}
        </Box>
      </DialogContent>

      <IconPickerModal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        value={iconKey}
        onSelect={(key) => { setIconKey(key); setIconError(false); }}
      />

      <DialogActions sx={{ justifyContent: 'center', p: 3, bgcolor: 'grey.50' }}>
        <Button onClick={onClose} sx={{ mr: 2 }}>
          <LocalText>Cancel</LocalText>
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!mapSpotId || !hasRequiredName(localizedPairs?.values)}
          size="large"
          sx={{ px: 6, py: 1.5, fontWeight: 700, borderRadius: 2 }}
        >
          <LocalText>Save</LocalText>
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateBoothModal;